import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, MailIcon, SendIcon, CheckCircle2Icon } from 'lucide-react';
export function SequenceHero() {
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 2500);
    return () => clearInterval(interval);
  }, []);
  const sequenceSteps = [{
    day: 'Day 1',
    type: 'Initial Email',
    subject: 'Quick question about {{company}}'
  }, {
    day: 'Day 4',
    type: 'Follow-up',
    subject: 'Re: Quick question about {{company}}'
  }, {
    day: 'Day 7',
    type: 'Break-up',
    subject: 'Checking in one last time'
  }];
  return <section className="relative pt-16 pb-24 overflow-hidden bg-white" data-id="element-956">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-957">
        <a href="#/features" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-8" data-id="element-958">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-959" /> All Features
        </a>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-960">
          {/* Left Column: Copy & CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-961">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.05]" data-id="element-962">
              Turn local leads into <br data-id="element-963" />
              <span className="text-blue-600" data-id="element-964">booked meetings</span>
            </h1>

            <p className="text-xl sm:text-2xl text-slate-600 mb-8 max-w-lg leading-relaxed" data-id="element-965">
              The best email sequence software for sales teams. Combine{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-blue-50 text-blue-700 text-lg sm:text-xl font-medium mx-1" data-id="element-966">
                verified data
              </span>{' '}
              with{' '}
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-green-50 text-green-700 text-lg sm:text-xl font-medium mx-1 mt-2 sm:mt-0" data-id="element-967">
                automated outreach
              </span>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-968">
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-10 py-4 text-white font-bold bg-blue-600 rounded-full hover:bg-blue-700 transition-colors text-lg text-center flex items-center justify-center gap-2" data-id="element-969">
                Try for free <ArrowRightIcon className="w-5 h-5" data-id="element-970" />
              </a>
            </div>

            {/* Static Stats Bar */}
            <div className="flex gap-12 pt-8" data-id="element-971">
              <div data-id="element-972">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-973">
                  50%+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-974">
                  Avg. open rates
                </div>
              </div>
              <div data-id="element-975">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-976">
                  10x
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-977">
                  Faster prospecting
                </div>
              </div>
              <div data-id="element-978">
                <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-979">
                  Native
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-980">
                  CRM sequences
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Live Data Preview Card */}
          <div className="relative" data-id="element-981">
            <div className="bg-slate-50 rounded-[2rem] p-8 sm:p-10 relative z-10 border border-slate-100 shadow-xl" data-id="element-982">
              <div className="flex justify-between items-center mb-8" data-id="element-983">
                <div data-id="element-984">
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-985">
                    B2B Email Sequence
                  </h3>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-986">
                    Campaign: Local Restaurants Q3
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-987">
                  <SendIcon className="w-6 h-6 text-blue-600 ml-1" data-id="element-988" />
                </div>
              </div>

              <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-989">
                {sequenceSteps.map((step, idx) => <div key={idx} className={`relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active transition-all duration-500 ${activeStep === idx ? 'opacity-100 scale-105' : 'opacity-50 scale-100'}`} data-id="element-990">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-blue-50 text-blue-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-991">
                      <MailIcon className="w-4 h-4" data-id="element-992" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-4 rounded-xl shadow-sm border border-slate-100" data-id="element-993">
                      <div className="flex items-center justify-between mb-2" data-id="element-994">
                        <span className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-995">
                          {step.day}
                        </span>
                        <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-996">
                          {step.type}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 truncate" data-id="element-997">
                        {step.subject}
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200/60 flex justify-between items-center" data-id="element-998">
                <div className="flex items-center gap-2 text-sm font-medium text-green-600 bg-green-50 px-3 py-1.5 rounded-full" data-id="element-999">
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-1000" /> 245 prospects
                  enrolled
                </div>
                <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1001">
                  Launch sequence
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}